import React from 'react';
import { Stack, Typography } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';

import type { StatusActivityStatus, StepIdentifier } from '@shared/patient-intake-status';

import { StatusIcon } from '../../components';
import { getStatusColour } from '../../utils';
import { getStepConfig } from '../../getStepConfig';
import type { TimelineItem } from '../HistoryDialog.types.ts';
import { TimelineItemSection } from '../TimelineItemSection.tsx';

type ShortDescriptionProps = {
  previousStatus: StatusActivityStatus;
  newStatus: StatusActivityStatus;
  stepIdentifier: StepIdentifier;
};

const ShortDescription = ({ previousStatus, newStatus, stepIdentifier }: ShortDescriptionProps) => (
  <Stack direction="row" alignItems="center" gap={0.5} justifyContent="center">
    <StatusIcon status={previousStatus} stepIdentifier={stepIdentifier} fontSize="small" />
    <Typography variant="caption">→</Typography>
    <StatusIcon status={newStatus} stepIdentifier={stepIdentifier} fontSize="small" />
  </Stack>
);

type StatusUpdateActionProps = {
  item: TimelineItem<'UPDATE_STATUS'>;
};

export const StatusUpdateAction: React.FC<StatusUpdateActionProps> = ({ item }) => {
  if (item.action !== 'UPDATE_STATUS') {
    return null;
  }

  if (!item.metadata) {
    return null;
  }

  const { previousStatus, newStatus } = item.metadata;

  if (!previousStatus || !newStatus) {
    return null;
  }

  const stepIdentifier = item.stepSnapshot.type;

  const stepConfig = getStepConfig(stepIdentifier);

  if (previousStatus === newStatus) {
    return (
      <Typography variant="caption" fontWeight="bold">
        No status change
      </Typography>
    );
  }

  const { label: previousStatusLabel } = stepConfig.statuses[previousStatus];
  const { label: newStatusLabel } = stepConfig.statuses[newStatus];

  const previousStatusColor = getStatusColour(previousStatus);
  const newStatusColor = getStatusColour(newStatus);

  return (
    <TimelineItemSection
      title="Status Updated"
      Icon={PlayArrowRoundedIcon}
      justifyContent="center"
      description={
        <ShortDescription previousStatus={previousStatus} newStatus={newStatus} stepIdentifier={stepIdentifier} />
      }
    >
      <Stack direction="row" alignItems="center" gap={0.5} justifyContent="flex-start">
        <Typography variant="caption">
          The step status went from{' '}
          <Typography variant="caption" fontWeight="bold" color={previousStatusColor.colour}>
            {previousStatusLabel}
          </Typography>{' '}
          to{' '}
          <Typography variant="caption" fontWeight="bold" color={newStatusColor.colour}>
            {newStatusLabel}
          </Typography>
        </Typography>
      </Stack>
    </TimelineItemSection>
  );
};
